import { axiosGet } from '@/API/API';
import NotifIcon from '@/assets/notif-icon.svg';
import SnsLogo from '@/assets/sobrussupplylogo.svg';
import NotifCard from '@/components/notifCard/NotifCard';
import { useAcl } from '@/hooks/useAcl';
import { fetchNotifs } from '@/store/notifSlice';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import { UserData } from '@/store/type';
import { Navigation } from '@/values/Navigation';

import { useEffect, useRef, useState } from 'react';
import { Link, NavLink as RouteLink, matchPath, useLocation, useNavigate } from 'react-router';

import {
	Button,
	Card,
	NavBarItem,
	NavBarItemLink,
	NavBarItems,
	NavBarLogo,
	NavBarSubItems,
	NavBarSubitem,
	NavBarSubitemLink,
	NavBarUser,
	NavBarUserLogout,
	NavBarUserProfile,
	Navbar,
} from '@sobrus-com/sobrus-design-system-v2';

import './style.scss';

type HeaderProps = {
	user: UserData | null;
};

const Header = ({ user }: HeaderProps) => {
	const [showNotifs, setShowNotifs] = useState(false);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { can, canIndex } = useAcl();
	const { pathname } = useLocation();
	const notifs = useAppSelector((state) => state.notif.notifs);
	const notifCardRef = useRef<HTMLDivElement>(null);
	const notifNavRef = useRef<HTMLDivElement>(null);

	const logOut = () => {
		axiosGet('auth/revoke').then(() => {
			localStorage.setItem('isAuthenticatedUser', 'false');
			navigate(0);
		});
	};

	useEffect(() => {
		if (!notifs) {
			dispatch(fetchNotifs(1));
		}
	}, [dispatch, notifs]);

	useEffect(() => {
		const outsideClick = (e: MouseEvent) => {
			if (
				showNotifs &&
				!notifNavRef.current?.contains(e.target as Node) &&
				notifCardRef.current &&
				!notifCardRef.current?.contains(e.target as Node)
			) {
				setShowNotifs(false);
			}
		};

		document.addEventListener('click', outsideClick);
		return () => document.removeEventListener('click', outsideClick);
	}, [showNotifs]);

	return (
		<header style={{ width: '100%' }}>
			{showNotifs && (
				<Card
					className="notification-menu-container"
					cardTitle="Dernières notifications"
					actionButton={
						<Button
							color="tertiary"
							size="sm"
							onClick={() => {
								navigate('/notifications');
								setShowNotifs(false);
							}}
						>
							Voir tout
						</Button>
					}
					ref={notifCardRef}
				>
					{notifs &&
						notifs?.notifications?.length > 0 &&
						notifs?.notifications?.map((ele) => (
							<NotifCard
								key={ele?.id}
								notifId={ele?.id}
								isSeen={ele?.isSeen}
								isClicked={ele?.isClicked}
								content={ele?.content}
								context={ele?.context}
								action={ele?.action}
								resourceId={ele?.resourceId}
								time={ele?.createdAt}
								setShowNotifs={setShowNotifs}
							/>
						))}
				</Card>
			)}
			<Navbar size="md">
				<NavBarLogo style={{ display: 'flex', alignItems: 'center' }}>
					<Link to="/home">
						<img style={{ display: 'block', maxWidth: 120 }} src={SnsLogo} alt="" />
					</Link>
					<div className="premiumText">{user?.subscriptionType === 'premium' ? 'Pro' : 'Free'}</div>
				</NavBarLogo>
				<NavBarItems>
					<div
						style={{ height: '100%' }}
						onClick={(e) => {
							setShowNotifs(!showNotifs);
						}}
						ref={notifNavRef}
					>
						<NavBarItem className="notification-wrapper">
							<div
								className="notification-container"
								onClick={(e) => e.preventDefault()}
								// style={(active) => (active.isActive ? { backgroundColor: "#fdedf1" } : null)}
							>
								<img
									src={NotifIcon}
									alt=""
									style={notifs && notifs?.unseenCount > 0 ? { marginRight: '-15px' } : {}}
									// style={{ marginRight: "-15px" }}
								/>
								{notifs && notifs?.unseenCount > 0 && (
									<div className="notif-number">{notifs?.unseenCount}</div>
								)}
							</div>
						</NavBarItem>
					</div>
					{Navigation?.map(
						(el, index) =>
							can(el?.does) && (
								<NavBarItem key={index} className="HeaderItem">
									<NavBarItemLink
										as={RouteLink}
										title={el.itemTitle}
										to={
											typeof el.to === 'string'
												? el?.to
												: el?.defaultToDoes
													? el?.to[canIndex(el?.does, el?.defaultToDoes)]
													: el?.to[canIndex(el?.does)]
										}
										className={
											el?.dropDown?.find((ele) =>
												matchPath({ path: `${ele?.to}/*` }, pathname)
											) && `active`
										}
										style={
											user?.subscriptionType === 'free' && el.premium
												? { opacity: 0.5, pointerEvents: 'none' }
												: {}
										}
									>
										{el.itemTitle}
									</NavBarItemLink>
									<NavBarSubItems>
										{el?.dropDown?.length !== 0 &&
											el?.dropDown?.map(
												(subEl, idx) =>
													can(subEl?.does) &&
													(user?.subscriptionType !== 'free' || !el.premium) && (
														<NavBarSubitem key={idx + 'subNavItem'}>
															{user?.subscriptionType === 'free' && subEl.premium ? (
																<NavBarSubitemLink
																	as={RouteLink}
																	title={subEl.itemTitle}
																	to="#"
																	className="nav-link"
																	style={{
																		opacity: 0.5,
																		color: 'gray',
																		backgroundColor: '#fff',
																	}}
																>
																	{subEl.itemTitle}
																</NavBarSubitemLink>
															) : (
																<NavBarSubitemLink
																	as={RouteLink}
																	title={subEl.itemTitle}
																	to={
																		typeof subEl.to === 'string'
																			? subEl?.to
																			: subEl?.to[canIndex(subEl?.does)]
																	}
																>
																	{subEl.itemTitle}
																</NavBarSubitemLink>
															)}
														</NavBarSubitem>
													)
											)}
									</NavBarSubItems>
									{user?.subscriptionType === 'free' && el.premium && (
										<div className="premiumDropDown" onClick={() => navigate('/premium')}>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="21.022"
												height="18.636"
												viewBox="0 0 21.022 18.636"
											>
												<path
													id="svgexport-10"
													d="M12.074,6.575c-.956,2.675-2.026,3.975-3,4.071A3.125,3.125,0,0,1,6.131,9.117a1.032,1.032,0,0,0-.191-.191,1.911,1.911,0,1,0-1.529.764l.019.229L6,16.111A2.867,2.867,0,0,0,8.768,18.29h8.485a2.867,2.867,0,0,0,2.771-2.179L21.591,9.92a1.032,1.032,0,0,0,.019-.229,1.911,1.911,0,1,0-1.529-.764,1.032,1.032,0,0,0-.191.172,3.273,3.273,0,0,1-2.943,1.548c-.956-.1-1.987-1.414-3-4.071a1.911,1.911,0,1,0-1.873,0Zm6.67,13.148a.956.956,0,0,1,0,1.911H7.278a.956.956,0,0,1,0-1.911Z"
													transform="translate(-2.5 -2.998)"
													fill="#f48240"
												/>
											</svg>
											Premium
										</div>
									)}
								</NavBarItem>
							)
					)}
				</NavBarItems>
				<NavBarUser fullName={`${user?.firstName} ${user?.lastName}`}>
					<NavBarUserProfile label="Profil" onClick={() => navigate('/settings')} />
					<NavBarUserLogout onClick={() => logOut()} />
				</NavBarUser>
				{/* end user infos  */}
			</Navbar>
		</header>
	);
};

export default Header;
